import { useState, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Table,
  Alert
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import {
  useParsePackingList,
  useCreateRawMaterials
} from '../../features/raw-materials/hooks/useRawMaterials.js';

const noSpinnerStyle = `
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .no-spinner { -moz-appearance: textfield; }
`;

const WIRE_TYPE_LABEL = {
  'SINGLE-WIRE': 'Solid',
  STRANDED: 'Stranded'
};

// ─── default header for manual entry ─────────────────────────────────
const defaultHeader = () => ({
  supplier: '',
  deliveryDate: '',
  deliveryNote: '',
  wireType: 'STRANDED',
  wireDiameter: 0.25,
  numberOfWires: 41,
  section: 2.5,
  packagingType: 'metal_reel',
  packagingModel: ''
});

const emptyItem = idx => ({
  _idx: idx,
  reelId: '',
  packNo: '',
  netWeight: 0,
  grossWeight: 0,
  tare: 0,
  _include: true
});

// ─── component ───────────────────────────────────────────────────────
const UploadPackingList = () => {
  const navigate = useNavigate();
  const parseMutation = useParsePackingList();
  const createMutation = useCreateRawMaterials();

  // mode: 'choose' | 'ai' | 'manual' | 'review'
  const [mode, setMode] = useState('choose');

  // AI upload state
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Shared state (both modes feed into this)
  const [header, setHeader] = useState(defaultHeader());
  const [items, setItems] = useState([]);
  const [sourceFile, setSourceFile] = useState('');
  const [nextIdx, setNextIdx] = useState(0);

  // ─── AI flow ───────────────────────────────────────────────────────
  const handleFiles = useCallback(files => {
    const f = files[0];
    if (f && f.type === 'application/pdf') {
      setFile(f);
    }
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleParse = async () => {
    if (!file) return;
    const result = await parseMutation.mutateAsync(file);
    const p = result.parsed;
    setSourceFile(result.sourceFile);

    setHeader({
      supplier: p.supplier || '',
      deliveryDate: p.deliveryDate || '',
      deliveryNote: p.deliveryNote || '',
      wireType: p.wireType || 'STRANDED',
      wireDiameter: p.wireDiameter || 0,
      numberOfWires: p.numberOfWires || 1,
      section: p.section || null,
      packagingType: p.packagingType || 'metal_reel',
      packagingModel: p.packagingModel || ''
    });

    const flatItems = (p.items || []).map((item, idx) => ({
      _idx: idx,
      reelId: item.reelId || '',
      packNo: item.packNo || '',
      netWeight: Number(item.netWeight) || 0,
      grossWeight: Number(item.grossWeight) || 0,
      tare: Number(item.tare) || 0,
      _include: true
    }));
    setItems(flatItems);
    setNextIdx(flatItems.length);
    setMode('review');
  };

  // ─── Manual flow ───────────────────────────────────────────────────
  const startManual = () => {
    setHeader(defaultHeader());
    setItems([emptyItem(0)]);
    setNextIdx(1);
    setSourceFile('manual');
    setMode('manual');
  };

  const addManualRow = () => {
    setItems(prev => [...prev, emptyItem(nextIdx)]);
    setNextIdx(prev => prev + 1);
  };

  const removeRow = idx => {
    setItems(prev => prev.filter(it => it._idx !== idx));
  };

  // ─── Shared helpers ────────────────────────────────────────────────
  const updateHeader = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));
  };

  const updateItem = (idx, field, value) => {
    setItems(prev =>
      prev.map(it => (it._idx === idx ? { ...it, [field]: value } : it))
    );
  };

  const toggleItem = idx => {
    setItems(prev =>
      prev.map(it =>
        it._idx === idx ? { ...it, _include: !it._include } : it
      )
    );
  };

  const toggleAll = () => {
    const allIncluded = items.every(it => it._include);
    setItems(prev => prev.map(it => ({ ...it, _include: !allIncluded })));
  };

  const includedItems = useMemo(
    () => items.filter(it => it._include),
    [items]
  );

  const totalNet = useMemo(
    () => includedItems.reduce((s, it) => s + (it.netWeight || 0), 0),
    [includedItems]
  );

  const handleConfirm = async () => {
    const toCreate = includedItems.map(({ _idx, _include, ...rest }) => ({
      ...rest,
      supplier: header.supplier,
      deliveryDate: header.deliveryDate || undefined,
      deliveryNote: header.deliveryNote || undefined,
      wireType: header.wireType,
      wireDiameter: header.wireDiameter,
      numberOfWires: header.numberOfWires,
      section: header.section || undefined,
      material: 'COPPER',
      packagingType: header.packagingType,
      packagingModel: header.packagingModel || undefined,
      sourceFile
    }));
    await createMutation.mutateAsync(toCreate);
    navigate('/apps/material-management/raw-materials');
  };

  const handleStartOver = () => {
    setMode('choose');
    setFile(null);
    setHeader(defaultHeader());
    setItems([]);
    setSourceFile('');
    parseMutation.reset();
    createMutation.reset();
  };

  // ─── render ────────────────────────────────────────────────────────
  const showReview = mode === 'review' || mode === 'manual';

  return (
    <div className="mb-9 mt-0">
      <style>{noSpinnerStyle}</style>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            {mode === 'manual' ? 'Manual Entry' : 'Upload Packing List'}
          </h2>
          <p className="text-700 mb-0">
            {mode === 'manual'
              ? 'Manually enter raw material items'
              : 'Upload a supplier packing list PDF or enter items manually'}
          </p>
        </div>
        <Button
          variant="outline-secondary"
          onClick={() =>
            navigate('/apps/material-management/raw-materials')
          }
        >
          Back to List
        </Button>
      </div>

      {/* ─── Step 0: Choose mode ─── */}
      {mode === 'choose' && (
        <Row className="g-4">
          <Col xs={12} md={6}>
            <Card
              className="h-100 text-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setMode('ai')}
            >
              <Card.Body className="py-5">
                <FontAwesomeIcon
                  icon="wand-magic-sparkles"
                  size="3x"
                  className="text-primary mb-3"
                />
                <h4>AI Extract from PDF</h4>
                <p className="text-700 mb-0">
                  Upload a packing list PDF and let AI extract all items
                  automatically
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card
              className="h-100 text-center"
              style={{ cursor: 'pointer' }}
              onClick={startManual}
            >
              <Card.Body className="py-5">
                <FontAwesomeIcon
                  icon="pen"
                  size="3x"
                  className="text-success mb-3"
                />
                <h4>Manual Entry</h4>
                <p className="text-700 mb-0">
                  Enter supplier info and items manually — no internet needed
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* ─── Step 1a: AI Upload ─── */}
      {mode === 'ai' && (
        <Card className="mb-4">
          <Card.Body>
            <div
              onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('pdf-input').click()}
              className="text-center py-5 rounded-3"
              style={{
                border: `2px dashed ${dragOver ? '#3874FF' : '#4a4f5a'}`,
                background: dragOver
                  ? 'rgba(56,116,255,0.05)'
                  : 'rgba(255,255,255,0.02)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <input
                id="pdf-input"
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={e => handleFiles(e.target.files)}
              />
              <FontAwesomeIcon
                icon="file-pdf"
                size="3x"
                className={dragOver ? 'text-primary mb-3' : 'text-500 mb-3'}
              />
              <div className="fs-8 fw-semibold mb-1">
                {file
                  ? file.name
                  : 'Drop packing list PDF here or click to browse'}
              </div>
              {file && (
                <div className="text-500 fs-9">
                  {(file.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>

            <div className="d-flex justify-content-center gap-3 mt-3">
              <Button variant="outline-secondary" onClick={handleStartOver}>
                Back
              </Button>
              {file && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleParse}
                  disabled={parseMutation.isPending}
                >
                  {parseMutation.isPending ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      AI is reading the PDF...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon="wand-magic-sparkles"
                        className="me-2"
                      />
                      Extract Items with AI
                    </>
                  )}
                </Button>
              )}
            </div>

            {parseMutation.isError && (
              <Alert variant="danger" className="mt-3 mb-0">
                {parseMutation.error?.response?.data?.error ||
                  parseMutation.error?.message ||
                  'Failed to parse PDF'}
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ─── Step 2: Header + Items (shared for AI review & manual) ─── */}
      {showReview && (
        <>
          {/* Editable header */}
          <Card className="mb-3">
            <Card.Body className="py-3">
              <Row className="g-3 align-items-end">
                <Col xs={12} sm={6} md={3}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Supplier
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    value={header.supplier}
                    onChange={e => updateHeader('supplier', e.target.value)}
                    placeholder="e.g. SARK BULGARIA"
                  />
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Wire Type
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={header.wireType}
                    onChange={e => updateHeader('wireType', e.target.value)}
                  >
                    <option value="STRANDED">Stranded</option>
                    <option value="SINGLE-WIRE">Solid (Single Wire)</option>
                  </Form.Select>
                </Col>
                <Col xs={6} sm={3} md={1}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Nr. Wires
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    className="no-spinner"
                    value={header.numberOfWires}
                    onChange={e =>
                      updateHeader('numberOfWires', Number(e.target.value))
                    }
                  />
                </Col>
                <Col xs={6} sm={3} md={1}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Diameter
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.01"
                    className="no-spinner"
                    value={header.wireDiameter}
                    onChange={e =>
                      updateHeader('wireDiameter', Number(e.target.value))
                    }
                  />
                </Col>
                <Col xs={6} sm={3} md={1}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Section
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.01"
                    className="no-spinner"
                    value={header.section || ''}
                    onChange={e =>
                      updateHeader(
                        'section',
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    placeholder="mm²"
                  />
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Packaging
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={header.packagingType}
                    onChange={e =>
                      updateHeader('packagingType', e.target.value)
                    }
                  >
                    <option value="metal_reel">Metal Reel</option>
                    <option value="octabin">Octabin</option>
                    <option value="wooden_pallet">Wooden Pallet</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Packaging Model
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    value={header.packagingModel}
                    onChange={e =>
                      updateHeader('packagingModel', e.target.value)
                    }
                    placeholder="e.g. K630"
                  />
                </Col>
              </Row>
              <Row className="g-3 align-items-end mt-1">
                <Col xs={6} sm={3} md={2}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Delivery Date
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={header.deliveryDate || ''}
                    onChange={e =>
                      updateHeader('deliveryDate', e.target.value)
                    }
                  />
                </Col>
                <Col xs={6} sm={3} md={2}>
                  <Form.Label className="fs-9 text-700 mb-1">
                    Delivery Note
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    value={header.deliveryNote}
                    onChange={e =>
                      updateHeader('deliveryNote', e.target.value)
                    }
                    placeholder="Invoice #"
                  />
                </Col>
                <Col xs="auto" className="ms-auto">
                  <Badge bg="primary" className="fs-9 px-3 py-2">
                    {includedItems.length} items | {totalNet.toFixed(0)} kg net
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Items table */}
          <Card className="mb-4">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="table-sm fs-9 mb-0" hover>
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: 40 }}>
                        <Form.Check
                          type="checkbox"
                          checked={
                            items.length > 0 &&
                            items.every(it => it._include)
                          }
                          onChange={toggleAll}
                        />
                      </th>
                      <th className="text-800">#</th>
                      <th className="text-800">Reel ID</th>
                      <th className="text-800">Pack No</th>
                      <th className="text-800 text-end">Gross (kg)</th>
                      <th className="text-800 text-end">Tare (kg)</th>
                      <th className="text-800 text-end">Net (kg)</th>
                      {mode === 'manual' && (
                        <th className="text-800 text-center" style={{ width: 50 }}></th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr
                        key={it._idx}
                        className={!it._include ? 'opacity-50' : ''}
                      >
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={it._include}
                            onChange={() => toggleItem(it._idx)}
                          />
                        </td>
                        <td className="text-500">{i + 1}</td>
                        <td>
                          <Form.Control
                            size="sm"
                            value={it.reelId}
                            onChange={e =>
                              updateItem(it._idx, 'reelId', e.target.value)
                            }
                            style={{ minWidth: 100 }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            value={it.packNo}
                            onChange={e =>
                              updateItem(it._idx, 'packNo', e.target.value)
                            }
                            style={{ minWidth: 100 }}
                          />
                        </td>
                        <td className="text-end">
                          <Form.Control
                            size="sm"
                            type="number"
                            className="text-end no-spinner"
                            value={it.grossWeight}
                            onChange={e =>
                              updateItem(
                                it._idx,
                                'grossWeight',
                                Number(e.target.value)
                              )
                            }
                            style={{ minWidth: 80 }}
                          />
                        </td>
                        <td className="text-end">
                          <Form.Control
                            size="sm"
                            type="number"
                            className="text-end no-spinner"
                            value={it.tare}
                            onChange={e =>
                              updateItem(
                                it._idx,
                                'tare',
                                Number(e.target.value)
                              )
                            }
                            style={{ minWidth: 80 }}
                          />
                        </td>
                        <td className="text-end">
                          <Form.Control
                            size="sm"
                            type="number"
                            className="text-end fw-semibold no-spinner"
                            value={it.netWeight}
                            onChange={e =>
                              updateItem(
                                it._idx,
                                'netWeight',
                                Number(e.target.value)
                              )
                            }
                            style={{ minWidth: 80 }}
                          />
                        </td>
                        {mode === 'manual' && (
                          <td className="text-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => removeRow(it._idx)}
                            >
                              <FontAwesomeIcon icon="trash" size="sm" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold">
                      <td colSpan={4} className="text-end">
                        Totals ({includedItems.length} items):
                      </td>
                      <td className="text-end">
                        {includedItems
                          .reduce((s, it) => s + (it.grossWeight || 0), 0)
                          .toFixed(0)}
                      </td>
                      <td className="text-end">
                        {includedItems
                          .reduce((s, it) => s + (it.tare || 0), 0)
                          .toFixed(0)}
                      </td>
                      <td className="text-end">{totalNet.toFixed(0)}</td>
                      {mode === 'manual' && <td></td>}
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Actions */}
          <div className="d-flex justify-content-between">
            <Button variant="outline-secondary" onClick={handleStartOver}>
              Start Over
            </Button>

            <div className="d-flex gap-2">
              {mode === 'manual' && (
                <Button variant="outline-primary" onClick={addManualRow}>
                  <FontAwesomeIcon icon="plus" className="me-2" />
                  Add Row
                </Button>
              )}
              <Button
                variant="success"
                size="lg"
                onClick={handleConfirm}
                disabled={
                  createMutation.isPending ||
                  includedItems.length === 0 ||
                  !header.supplier
                }
              >
                {createMutation.isPending ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="check" className="me-2" />
                    Confirm & Save {includedItems.length} Items
                  </>
                )}
              </Button>
            </div>
          </div>

          {createMutation.isError && (
            <Alert variant="danger" className="mt-3">
              {createMutation.error?.response?.data?.error ||
                createMutation.error?.message ||
                'Failed to save'}
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

export default UploadPackingList;
